import React, {
    useEffect,
    useImperativeHandle,
    useRef,
} from 'react';

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export interface ITurnstileRenderOptions {
    sitekey: string;
    callback: (token: string) => void;
    'expired-callback'?: () => void;
    'error-callback'?: () => void;
    theme?: 'auto' | 'light' | 'dark';
    action?: string;
    cData?: string;
}

export interface ITurnstileApi {
    render: (container: HTMLElement, options: ITurnstileRenderOptions) => string;
    remove: (widgetId: string) => void;
    reset: (widgetId: string) => void;
}

const getTurnstile = (): ITurnstileApi | undefined =>
    (window as unknown as { turnstile?: ITurnstileApi }).turnstile;

let scriptPromise: Promise<void> | undefined;

function loadTurnstileScript(): Promise<void> {
    if (getTurnstile()) {
        return Promise.resolve();
    }

    if (!scriptPromise) {
        scriptPromise = new Promise<void>((resolve, reject) => {
            const fail = () => {
                // Clear the cached promise so a later mount can retry instead of reusing a rejected one.
                scriptPromise = undefined;
                reject(new Error('Failed to load Turnstile script'));
            };

            const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
            if (existing) {
                existing.addEventListener('load', () => resolve());
                existing.addEventListener('error', fail);
                return;
            }

            const script = document.createElement('script');
            script.src = TURNSTILE_SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = fail;
            document.head.appendChild(script);
        });
    }

    return scriptPromise;
}

export interface IUiTurnstileProps {
    siteKey?: string;
    action: string;
    cData?: string;
    onVerify: (token: string) => void;
    onExpire?: () => void;
    onError?: () => void;
    onUnmount?: () => void;
    className?: string;
    'data-cy'?: string;
}

export interface IUiTurnstileHandle {
    reset: () => void;
}

export const UiTurnstile = React.forwardRef<IUiTurnstileHandle, IUiTurnstileProps>(({
    siteKey, action, cData, onVerify, onExpire, onError, onUnmount, className, 'data-cy': dataCy,
}, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | undefined>(undefined);

    // Hold callbacks in refs so the widget mounts once and isn't recreated when the parent
    // passes new inline callbacks on each render.
    const onVerifyRef = useRef(onVerify);
    const onExpireRef = useRef(onExpire);
    const onErrorRef = useRef(onError);
    const onUnmountRef = useRef(onUnmount);
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
    onUnmountRef.current = onUnmount;

    useImperativeHandle(ref, () => ({
        reset: () => {
            const turnstile = getTurnstile();
            if (widgetIdRef.current && turnstile) {
                turnstile.reset(widgetIdRef.current);
            }
        },
    }), []);

    useEffect(() => {
        let cancelled = false;

        loadTurnstileScript()
            .then(() => {
                const turnstile = getTurnstile();
                if (cancelled || !containerRef.current || !turnstile) {
                    return;
                }

                if (!siteKey) {
                    // No site key configured for this env — fail via onError instead of
                    // silently falling back to an always-pass test key.
                    onErrorRef.current?.();
                    return;
                }

                widgetIdRef.current = turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    action,
                    ...(cData ? { cData } : {}),
                    theme: 'auto',
                    callback: (token: string) => onVerifyRef.current(token),
                    'expired-callback': () => onExpireRef.current?.(),
                    'error-callback': () => onErrorRef.current?.(),
                });
            })
            .catch(() => onErrorRef.current?.());

        return () => {
            cancelled = true;
            const turnstile = getTurnstile();
            if (widgetIdRef.current && turnstile) {
                turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = undefined;
            }

            onUnmountRef.current?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            ref={containerRef}
            className={className}
            data-cy={dataCy ?? 'ui-turnstile'} />
    );
});

UiTurnstile.displayName = 'UiTurnstile';

export default UiTurnstile;
